import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION = 'progress';

function progressId(userId, courseId) {
  return `${userId}_${courseId}`;
}

/**
 * Get progress for one course: { completedLessonIds: string[], progressPercent: number }.
 */
export async function getProgress(userId, courseId) {
  if (!userId || !courseId) return { completedLessonIds: [], progressPercent: 0 };
  const ref = doc(db, COLLECTION, progressId(userId, courseId));
  const snap = await getDoc(ref);
  if (!snap.exists()) return { completedLessonIds: [], progressPercent: 0 };
  const d = snap.data();
  const raw = Array.isArray(d.completedLessonIds) ? d.completedLessonIds : [];
  const completedLessonIds = raw.map((id) => String(id));
  const progressPercent = typeof d.progressPercent === 'number' ? d.progressPercent : 0;
  return { completedLessonIds, progressPercent };
}

/**
 * Get all progress docs for a user. Returns Map<courseId, { completedLessonIds, progressPercent }>.
 */
export async function getAllProgressForUser(userId) {
  if (!userId) return new Map();
  const { collection, query, where, getDocs } = await import('firebase/firestore');
  const q = query(collection(db, COLLECTION), where('userId', '==', userId));
  const snap = await getDocs(q);
  const map = new Map();
  snap.forEach((docSnap) => {
    const d = docSnap.data();
    const cid = d.courseId;
    if (cid) {
      map.set(cid, {
        completedLessonIds: Array.isArray(d.completedLessonIds) ? d.completedLessonIds : [],
        progressPercent: typeof d.progressPercent === 'number' ? d.progressPercent : 0,
      });
    }
  });
  return map;
}

/**
 * Mark a lesson complete and recalc progress. totalLessons = total count in course.
 * Returns new progressPercent (0–100). If 100%, caller can issue certificate.
 */
export async function setLessonComplete(userId, courseId, lessonId, totalLessons) {
  if (!userId || !courseId || !lessonId || totalLessons < 1) return 0;
  const ref = doc(db, COLLECTION, progressId(userId, courseId));
  const snap = await getDoc(ref);
  const existing = snap.exists() ? snap.data() : { completedLessonIds: [], progressPercent: 0 };
  let completedLessonIds = Array.isArray(existing.completedLessonIds)
    ? existing.completedLessonIds.map((id) => String(id))
    : [];
  const idStr = String(lessonId);
  if (!completedLessonIds.includes(idStr)) {
    completedLessonIds.push(idStr);
  }
  const progressPercent = Math.min(100, Math.round((completedLessonIds.length / totalLessons) * 100));
  await setDoc(ref, {
    userId,
    courseId,
    completedLessonIds,
    progressPercent,
    updatedAt: serverTimestamp(),
  });
  return progressPercent;
}
